import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMediaQueryMatchesOnMobile, useMediaQueryMatchesOnDesktop } from '@yonderland/andes-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BUCKET_FILTER, MULTISELECT_FILTER, } from '@as-react/common-types';
import { Box, Button, Group, GroupAlign, Image, Popover, PopoverHeight, PopoverType, ScrollableList, ScrollableListVariant, Text, TextSize, } from '@as/cotopaxi';
import { getBucketFilter, getFilterQueryParameter, getMultiFilter } from '../../../../util/FilterUtil';
import { FilterWizardItem } from './FilterWizardItem';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { useDispatch } from 'react-redux';
import { analyticsActions } from '../../../../redux/analytics/actions/AnalyticsActions.creators';
export const FilterWizardModal = ({ anchorEl, fetchSearchResultsCount, filter, filterWizardDescription, filterWizardImage, filterWizardTitle, filterWizardValues, handlePopoverClose, itemsCount, updateFilters, urlFilters, activeWizardFilterKeys, setActiveWizardFilterKeys, }) => {
    const [hasFilterUpdated, setFilterUpdated] = useState(false);
    const isMobile = useMediaQueryMatchesOnMobile();
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const dispatch = useDispatch();
    const getFilterQueryParams = (tmpSelectedFilterKeys, filterOn, filterType) => {
        var _a;
        const defaultFilter = urlFilters.defaultFilter ? getFilterQueryParameter(urlFilters.defaultFilter) : '';
        const appliedFilters = ((_a = urlFilters.filter) === null || _a === void 0 ? void 0 : _a.length)
            ? urlFilters.filter.reduce((query, tmpFilter) => tmpFilter.filterOn === filterOn
                ? query
                : query + getFilterQueryParameter(`${tmpFilter.filterOn}:${tmpFilter.filterValue}`), '')
            : '';
        const wizardFilters = tmpSelectedFilterKeys.length
            ? filterType === MULTISELECT_FILTER
                ? getMultiFilter(filterOn, tmpSelectedFilterKeys)
                : getBucketFilter(filterOn, tmpSelectedFilterKeys[0])
            : '';
        return `${defaultFilter}${appliedFilters}${wizardFilters}`.substring(1); // remove '&' from beginning of the string
    };
    const updateResultsCount = (query) => {
        fetchSearchResultsCount(query);
    };
    const onSelectChange = (filterKey) => {
        let newActiveWizardFilterKeys = [];
        if (filter.type === MULTISELECT_FILTER) {
            const selectedValueIndex = activeWizardFilterKeys.indexOf(filterKey);
            if (selectedValueIndex === -1) {
                newActiveWizardFilterKeys = [...activeWizardFilterKeys, filterKey];
            }
            else {
                newActiveWizardFilterKeys = [...activeWizardFilterKeys];
                newActiveWizardFilterKeys.splice(selectedValueIndex, 1);
            }
        }
        else {
            if (!activeWizardFilterKeys.includes(filterKey)) {
                newActiveWizardFilterKeys.push(filterKey);
            }
        }
        setActiveWizardFilterKeys(newActiveWizardFilterKeys);
        setFilterUpdated(true);
        updateResultsCount(getFilterQueryParams(newActiveWizardFilterKeys, filter.filterOn, filter.type));
    };
    const applyFilters = () => {
        const filterType = filter.type === BUCKET_FILTER ? BUCKET_FILTER : MULTISELECT_FILTER;
        updateFilters({
            filterOn: filter.filterOn,
            filterOnName: filter.name,
            filteredItems: filter.values.map((filterValue) => ({
                filterKey: filterValue.filterKey,
                filterKeyName: filterValue.name,
                filterOn: filter.filterOn,
                filterOnName: filter.name,
                filterType: filterType,
                isActive: activeWizardFilterKeys.includes(filterValue.filterKey),
            })),
            filterType: filterType,
            filterDisplayHint: filter.displayHint,
            filterContentType: filter.filterContentType,
            multiUpdate: true,
        });
        // Only call when user made changes to the filters.
        hasFilterUpdated && dispatch(analyticsActions.filterWizardSelected(filter.filterOn));
        handlePopoverClose();
    };
    useEffect(() => {
        window.addEventListener('popstate', handlePopoverClose);
        return () => window.removeEventListener('popstate', handlePopoverClose);
    }, []);
    if (!filter.values) {
        return null;
    }
    return (_jsx(Popover, { anchorEl: anchorEl, type: !isDesktop ? PopoverType.FILTERS_WIZARD : PopoverType.MODAL, onClose: handlePopoverClose, height: PopoverHeight.LARGE, title: filterWizardTitle || filter.name, footerElement: _jsx(Box, { tablet: { spacing: { paddingLeft: 2, paddingRight: 2 } }, children: _jsx(Group, { align: GroupAlign.RIGHT, children: _jsx(Button, { fill: isMobile, text: t('filterWizard.button.showResults', {
                        count: itemsCount,
                    }), onClick: applyFilters }) }) }), children: _jsx(ScrollableList, { variant: ScrollableListVariant.SHADOW, children: _jsxs(Box, { mobile: { spacing: { marginLeft: 2, marginRight: 2 } }, tablet: { spacing: { marginLeft: 4, marginRight: 4 } }, children: [(!!filterWizardDescription || !!filterWizardImage) && (_jsxs(Box, { mobile: { spacing: { marginBottom: 1 } }, children: [!!filterWizardImage && (_jsx(Box, { mobile: { spacing: { paddingBottom: 1 } }, tablet: { spacing: { paddingBottom: 2 } }, children: _jsx(Image, { src: filterWizardImage, lazy: true }) })), !!filterWizardDescription && (_jsx(Box, { mobile: { spacing: { paddingBottom: 1 } }, tablet: { spacing: { paddingBottom: 2 } }, children: _jsx(Text, { inline: true, size: TextSize.SMALL, children: filterWizardDescription }) }))] })), filter.values.map((filterValue) => {
                        const filterWizardValue = filterWizardValues.find((filerWizardValue) => filerWizardValue.filterKey === filterValue.filterKey);
                        const isSelected = activeWizardFilterKeys.includes(filterValue.filterKey);
                        const _onSelectChange = () => onSelectChange(filterValue.filterKey);
                        return (_jsx(FilterWizardItem, { checked: isSelected, filterKey: filterValue.filterKey, title: (filterWizardValue === null || filterWizardValue === void 0 ? void 0 : filterWizardValue.title) || filterValue.name, itemCount: filterValue.itemCount, imageURL: filterWizardValue === null || filterWizardValue === void 0 ? void 0 : filterWizardValue.image, description: filterWizardValue === null || filterWizardValue === void 0 ? void 0 : filterWizardValue.description, onChange: _onSelectChange, multiSelect: filter.type === MULTISELECT_FILTER }, filterValue.filterKey));
                    })] }) }) }));
};
